import type { QtoSourceId } from "./types";
import { qtoTagForClassification } from "./utils";
import { QTO_REVIEW_TYPE_WARN, inferWarnKind, type ReviewRow } from "./reviewRow";

export type LineItemsCsvOptions = {
  /** Which QTO integration is submitting (drives default filenames and `import_batch` labels). */
  qtoSourceId: QtoSourceId;
  /** Overrides default Add CSV `import_batch` (`{label} Import - yyyy/MM/dd HH:mm:ss`). */
  importBatch?: string;
  /** Overrides default Update CSV `import_batch` (`{label} update - yyyy/MM/dd HH:mm:ss`). */
  updateImportBatch?: string;
  /** Overrides default Add multipart filename. */
  addFilename?: string;
  /** Overrides default Update multipart filename. */
  updateFilename?: string;
};

const ADD_CSV_HEADERS = ["name", "product", "quantity", "uom", "order", "notes", "import_batch"] as const;

const UPDATE_CSV_HEADERS = ["id", "quantity", "import_batch"] as const;

function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function reviewedLineItemFields(row: ReviewRow): {
  name: string;
  uomRaw: string;
  uomLower: string;
  quantity: number;
} {
  // Always the takeoff name: `lineItemName` holds the linked product's name on Add rows, which would
  // override the takeoff name. A blank name still falls back to the product's name on import.
  const name = row.takeoffName;
  const uomRaw = row.lineItemUom || row.takeoffUom;
  const uomLower = uomRaw.toLowerCase();
  const quantity = row.lineItemQty || row.takeoffQty;
  return { name, uomRaw, uomLower, quantity };
}

/** QTO link tag in `notes` (matches `qtoTagForClassification` / `qtoIdFromLineItemNotes`). */

function qtoNotesForCsv(row: ReviewRow): string {
  const id = row.qtoId?.trim();
  if (!id) return "";
  return qtoTagForClassification({ id });
}

/**
 * Add rows: `name`, `product`, `quantity`, `uom`, `order`, `notes`, `import_batch`.
 * When `product` is set, `uom` is usually omitted—except WARN `uom_mismatch`, where takeoff UOM is sent explicitly.
 */

export function buildLineItemsAddCsv(rows: ReviewRow[], importBatch: string): string {
  const adds = rows.filter((r) => r.action === "Add");
  const csvRows = adds.map((row) => {
    const order = row.order?.trim() ?? "";
    const notes = qtoNotesForCsv(row);

    const warnKindEff =
      row.link === QTO_REVIEW_TYPE_WARN ? (row.warnKind ?? inferWarnKind(row.link, row.lineItemName)) : undefined;

    let name: string;
    let product: string;
    let uomLower: string;
    let quantity: number;
    let forceUomWithProduct = false;

    if (warnKindEff === "broken_product" || warnKindEff === "no_upc_link") {
      name = row.takeoffName;
      product = "";
      uomLower = String(row.takeoffUom || "").toLowerCase();
      quantity = row.takeoffQty;
    } else if (warnKindEff === "uom_mismatch") {
      const fields = reviewedLineItemFields(row);
      name = fields.name;
      product = row.productId.trim();
      uomLower = String(row.takeoffUom || "").toLowerCase();
      quantity = fields.quantity;
      forceUomWithProduct = true;
    } else {
      const fields = reviewedLineItemFields(row);
      name = fields.name;
      uomLower = fields.uomLower;
      quantity = fields.quantity;
      product = row.link === "Product" && row.productId.trim() !== "" ? row.productId.trim() : "";
    }

    const uomCell = product && !forceUomWithProduct ? "" : uomLower ? escapeCsvField(uomLower) : "";

    return [
      escapeCsvField(name),
      product ? escapeCsvField(product) : "",
      quantity ? String(quantity) : "",
      uomCell,
      order ? escapeCsvField(order) : "",
      notes ? escapeCsvField(notes) : "",
      escapeCsvField(importBatch),
    ].join(",");
  });
  return [ADD_CSV_HEADERS.join(","), ...csvRows].join("\r\n");
}

/** Update rows: `id`, `quantity`, `import_batch` (`id` = hidden line item id). */

export function buildLineItemsUpdateCsv(rows: ReviewRow[], importBatch: string): string {
  const updates = rows.filter((r) => r.action === "Update");
  const batchCell = escapeCsvField(importBatch);
  const csvRows = updates.map((row) => {
    const qty = row.takeoffQty;
    return [escapeCsvField(row.lineItemId ?? ""), qty ? String(qty) : "", batchCell].join(",");
  });
  return [UPDATE_CSV_HEADERS.join(","), ...csvRows].join("\r\n");
}

/**
 * POST CSV to the ediphi line-items import endpoint as multipart/form-data.
 */
